import {useContext, useMemo, useRef, useState} from "react";
import {DialogContent, DialogTitle, Grid, Tab, Tabs, TextField} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import moment from "moment";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {SmallGreyButton} from "../../../../misc/SmallGreyButton";
import {useFilteredTableData} from "../../../../misc/scroller/Table";
import {PDFInfo, PrintManagerContext} from "../../../../misc/pdf/PrintManager";
import {useSubmittalPreferences} from "../../../../misc/pdf/useSubmittalPreferences";
import {InventoryRequest} from "../../../../api/Logistics";
import {Project} from "../../../../api/Projects";
import {Company} from "../../../../api/Companies";
import {Contact} from "../../../../api/Contacts";
import {useProject, useProjectId} from "../ProjectName";
import {useCurrentContractor} from "../../quote/CurrentContractor";
import {ColumnTab} from "../openings/pdf/ColumnTab";
import {PreambleTab} from "../openings/pdf/PreambleTab";
import {hasPreambleContent} from "../../../../misc/richtext/preambleContent";
import {TabWrapper} from "../TabWrapper";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {prepsAsString, woodDetailAsString} from "../../../logistics/warehouse/InventoryFrameTable";
import {ucWords} from "./PriceRequestSubmittal";
import {logisticsTableName} from "./Logistics";
import {LogisticsRecordsPDF} from "./LogisticsRecordsPDF";

export type LogisticsExportColumn = {
    display: string;
    key: string;
    render?: (input: InventoryRequest) => string;
}

const tabDisplayNames: {[key: string]: string} = {
    "frame-hm": "HM Frame",
    "frame-anchor": "Anchors",
    "door-hm": "HM Door",
    "door-wd": "WD Door",
    "hardware": "Hardware",
    "div10": "Div 10",
    "component": "Component",
}

export type LogisticsRecordsType = "frame-hm" | "frame-anchor" | "door-hm" | "door-wd" | "hardware" | "div10" | "component";

export const isLogisticsRecordsTab = (tab: string) => {
    return tab in tabDisplayNames;
}

export function logisticsRecordsColumns(type: LogisticsRecordsType): LogisticsExportColumn[] {
    const common: LogisticsExportColumn[] = [{
        display: "Qty",
        key: "qty",
    },{
        display: "Status",
        key: "status",
        render: r => ucWords(r.status.replace(/-/g, " ")),
    },{
        display: "Source",
        key: "source",
        render: r => {
            if(r.overrideSource === "purchase") return "Purchase";
            if(r.overrideSource === "non-stocking") return "Warehouse";
            return "";
        },
    },{
        display: "Needed By",
        key: "neededBy",
        render: r => r.neededBy ? moment.utc(r.neededBy).format("MMM D, YYYY") : "",
    },{
        display: "Delivered",
        key: "deliveredAt",
        render: r => r.deliveredAt ? moment.utc(r.deliveredAt).format("MMM D, YYYY") : "",
    }];

    if(type === "div10") {
        return [{
            display: "Room",
            key: "room",
        },{
            display: "Type",
            key: "category",
        },{
            display: "Product",
            key: "productCategoryName",
            render: r => r.productCategoryName || "",
        },{
            display: "Code",
            key: "productCode",
        }, ...common];
    }

    const showOpeningInfo = type !== "component";

    const list: (LogisticsExportColumn|false)[] = [
        showOpeningInfo && {
            display: "Seq #",
            key: "seqNumber",
        },{
            display: "Opening",
            key: "openingName",
        },
        showOpeningInfo && {
            display: "Floor",
            key: "floor",
        },{
            display: "Ext",
            key: "exterior",
            render: r => r.exterior ? "Yes" : "No",
        },{
            display: "Product",
            key: "name",
        },{
            display: "Code",
            key: "productCode",
            render: r => {
                if(r.productCode === "Keying" && r.keyingType) return `${r.productCode} (${ucWords(r.keyingType.replace("-", " "))})`;
                return r.productCode;
            },
        },{
            display: "Finish",
            key: "finish",
        },{
            display: "Dim",
            key: "dimensions",
        },
        (type === "door-hm" || type === "door-wd") && {
            display: "Door Elev",
            key: "doorElevation",
            render: r => r.door?.elevation || "",
        },
        type === "frame-hm" && {
            display: "Screen Elev",
            key: "screenElev",
            render: r => r.frame?.screenElev || "",
        },
        type !== "hardware" && {
            display: "Preps",
            key: "preps",
            render: r => {
                if(r.type === "door") return prepsAsString(r.door?.preps);
                if(r.type === "frame") return prepsAsString(r.frame?.preps);
                return "";
            },
        },
        type === "door-wd" && {
            display: "Wood Detail",
            key: "woodDetail",
            render: r => r.type === "door" ? woodDetailAsString(r.door?.woodDetail) : "",
        },
        ...common,
    ];

    return list.filter(v => !!v) as LogisticsExportColumn[];
}

function defaultColumnKeys(type: LogisticsRecordsType): string[] {
    if(type === "div10") {
        return ["room", "category", "productCategoryName", "productCode", "qty", "status", "neededBy", "deliveredAt"];
    }

    return ["seqNumber", "openingName", "floor", "name", "productCode", "finish", "dimensions", "qty", "status", "neededBy", "deliveredAt"];
}

type LogisticsRecordsPrefs = {
    version: 1;
    columnsByType: {[type: string]: string[]};
    notes: string;
}

export function LogisticsRecordsSubmittal(props: {
    tab: string;
}) {
    const [show, setShow] = useState(false);

    if(!isLogisticsRecordsTab(props.tab)) return null;

    return (
        <Grid item>
            <SmallGreyButton onClick={() => setShow(true)} startIcon={<AddCircleIcon />}>
                Logistics Records
            </SmallGreyButton>
            {show && <MakeLogisticsRecordsSubmittal tab={props.tab as LogisticsRecordsType} onDone={() => setShow(false)} />}
        </Grid>
    )
}

export function MakeLogisticsRecordsSubmittal(props: {
    tab: LogisticsRecordsType;
    onDone(): void;
}) {
    const projectId = useProjectId();
    const project = useProject();
    const printCtx = useContext(PrintManagerContext);
    const rows = useFilteredTableData<InventoryRequest>(logisticsTableName(projectId, props.tab));

    const columns = useMemo(() => logisticsRecordsColumns(props.tab), [props.tab]);
    const [selected, setSelected] = useState<LogisticsExportColumn[]>(
        defaultColumnKeys(props.tab)
            .map(key => columns.find(c => c.key === key))
            .filter(v => !!v) as LogisticsExportColumn[]
    );
    const [title, setTitle] = useState("Logistics Records");
    const [notes, setNotes] = useState("");
    const [tab, setTab] = useState("columns");
    const loadedPrefs = useRef<LogisticsRecordsPrefs|null>(null);

    const prefs = useSubmittalPreferences({
        project: projectId,
        kind: "logistics-records",
        onLoad: (value: LogisticsRecordsPrefs) => {
            if(!value || value.version !== 1) return;

            loadedPrefs.current = value;
            setNotes(value.notes || "");

            const keys = value.columnsByType && value.columnsByType[props.tab];
            if(keys) {
                setSelected(keys
                    .map(key => columns.find(c => c.key === key))
                    .filter(v => !!v) as LogisticsExportColumn[]);
            }
        }
    });

    const {getContractorDetails, CurrentContractorPromptElement} = useCurrentContractor({
        onAutoPromptCanceled: props.onDone
    });

    if(CurrentContractorPromptElement) {
        return (<>{CurrentContractorPromptElement}</>);
    }

    if(prefs.loading) return null;

    const submit = async () => {
        await prefs.save({
            version: 1,
            columnsByType: {
                ...(loadedPrefs.current?.columnsByType || {}),
                [props.tab]: selected.map(c => c.key),
            },
            notes: notes,
        });

        if(!project) return;

        const {client, clientContact} = await getContractorDetails();

        await printCtx.print2(getLogisticsRecordsPDF({
            projectId: projectId,
            getData: async () => ({
                project: project,
                client: client,
                clientContact: clientContact,
                lastSubmittal: prefs.lastSubmission,
                title: title,
                notes: notes,
                columns: selected,
                data: rows,
            })
        }));

        props.onDone();
    };

    return (
        <Dialog2 open maxWidth="md" fullWidth onClose={props.onDone} onEnterPress={submit}>
            <DialogTitle>Create Logistics Records Submittal</DialogTitle>
            <div style={{paddingLeft: 24, paddingRight: 24, paddingBottom: 16}}>
                <TextField
                    label="Title"
                    fullWidth
                    size="small"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                />
            </div>
            <TabWrapper>
                <Tabs className={tabGroup} value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab label="Columns" value="columns" />
                    <Tab label={
                        <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                            Notes
                            {hasPreambleContent(notes) && (
                                <span style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: "#1976d2",
                                    display: "inline-block",
                                }} />
                            )}
                        </span>
                    } value="notes" />
                </Tabs>
            </TabWrapper>
            <DialogContent>
                <div style={{height: 8}} />
                {tab === "notes" ?
                    <PreambleTab value={notes} onChange={setNotes} /> :
                    <ColumnTab
                        value={selected}
                        onChange={setSelected}
                        columns={columns}
                        sample={rows[0]}
                        renderCell={(col, sample) => col.render ? col.render(sample) : (sample[col.key]?.toString() || "")}
                    />}
            </DialogContent>
            <SmoothActionBar
                onCancel={props.onDone}
                onSubmit={submit}
                actionName="Create and Download"
            />
        </Dialog2>
    )
}

export function getLogisticsRecordsPDF(props: {
    projectId: number;
    getData: () => Promise<{
        project: Project;
        client?: Company;
        clientContact?: Contact;
        lastSubmittal: string | null;
        title: string;
        notes: string;
        columns: LogisticsExportColumn[];
        data: InventoryRequest[];
    }>
}): PDFInfo {
    return {
        name: "logistics-records.pdf",
        render: async onReady => {
            const d = await props.getData();

            return (
                <LogisticsRecordsPDF
                    project={d.project}
                    client={d.client}
                    clientContact={d.clientContact}
                    lastSubmittal={d.lastSubmittal}
                    title={d.title}
                    notes={d.notes}
                    columns={d.columns}
                    data={d.data}
                    onReady={onReady}
                />
            );
        },
        serverInfo: {
            submissionForProject: props.projectId,
            submissionType: "logistics-records",
        }
    }
}
